/**
 * Cart / checkout "Commonly researched together" recommendation engine.
 *
 * DETERMINISTIC, catalog-only, no protocols, no medical claims.  Every
 * mapping below reflects publicly discussed research pairings; the
 * shopper-visible copy still calls it "commonly researched together"
 * and disclaims that it is NOT evidence of safe combined use.
 *
 * Priority rule (owner-mandated, outranks every other trigger):
 *   * If any GLP/incretin product is already in the cart, recommend
 *     — in this EXACT order — MOTS-C, AOD-9604, GHK-Cu, Tesamorelin.
 *     Never suggest a second GLP/incretin.
 *
 * Everything is a pure function: `recommendForCart(cartItems, catalog, max)`.
 * Callers pass live catalog rows so image / price / slug / stock / PDP
 * link are always accurate.
 */
/* eslint-disable no-underscore-dangle */

// ─── Compound-family regex table ────────────────────────────────────
//
// Each slug in the catalog is scanned for every regex below.  A slug
// can match MORE than one family (blends: `bpc-157-tb-500-*`, glp-2t
// + glp-3r co-packs, cjc-1295 + ipamorelin, etc.).  Every recommended
// item is filtered so that NONE of its family keys overlap with a
// cart item's family keys OR another already-picked recommendation.

const FAMILIES = Object.freeze({
  // GLP / incretin — collapse every glp-Nx or blend under one key.
  "glp":              /(?:^|-)glp-\d[a-z]?(?:-|$)/,

  // Recovery peptides
  "bpc-157":          /(?:^|-)bpc-157(?:-|$)/,
  "tb-500":           /(?:^|-)tb-500(?:-|$)/,

  // Copper / skin complexes
  "ghk-cu":           /(?:^|-)ghk-cu(?:-|$)/,
  "ahk-cu":           /(?:^|-)ahk-cu(?:-|$)/,

  // Immune / antimicrobial
  "kpv":              /(?:^|-)kpv(?:-|$)/,
  "thymosin-alpha-1": /(?:^|-)thymosin-alpha-1(?:-|$)/,

  // GH axis
  "cjc-1295":         /(?:^|-)cjc-1295(?:-|$)/,
  "ipamorelin":       /(?:^|-)ipamorelin(?:-|$)/,
  "sermorelin":       /(?:^|-)sermorelin(?:-|$)/,
  "hexarelin":        /(?:^|-)hexarelin(?:-|$)/,
  "tesamorelin":      /(?:^|-)tesamorelin(?:-|$)/,
  "igf-1":            /(?:^|-)igf-1(?:-|$)/,

  // Mitochondrial / longevity
  "mots-c":           /(?:^|-)mots-c(?:-|$)/,
  "aod-9604":         /(?:^|-)aod-9604(?:-|$)/,
  "epithalon":        /(?:^|-)epi(?:t|thal)on(?:-|$)/,   // accepts Epitalon spelling
  "ss-31":            /(?:^|-)ss-31(?:-|$)/,
  "humanin":          /(?:^|-)humanin(?:-|$)/,

  // Neuropeptides
  "semax":            /(?:^|-)semax(?:-|$)/,
  "selank":           /(?:^|-)selank(?:-|$)/,
  "dsip":             /(?:^|-)dsip(?:-|$)/,

  // Other
  "cagrilintide":     /(?:^|-)cagrilintide(?:-|$)/,
  "pt-141":           /(?:^|-)pt-141(?:-|$)/,
  "oxytocin":         /(?:^|-)oxytocin(?:-|$)/,
  "kisspeptin":       /(?:^|-)kisspeptin(?:-|$)/,
});

/**
 * Return every family key that matches this slug (0..N).  Order is the
 * insertion order of the FAMILIES table above — do NOT rely on it for
 * priority; priority comes from the RULES table.
 */
export function compoundKeysForSlug(slug) {
  if (!slug || typeof slug !== "string") return [];
  const keys = [];
  for (const [key, rx] of Object.entries(FAMILIES)) {
    if (rx.test(slug)) keys.push(key);
  }
  return keys;
}

// ─── Trigger → target priority list ─────────────────────────────────
// Owner-authoritative mappings.  Keys are family keys; values are the
// EXACT priority order for that trigger.

const RULES = Object.freeze({
  // Priority rule #1 — GLP outranks everything, enforced in engine below.
  "glp":              Object.freeze(["mots-c", "aod-9604", "ghk-cu", "tesamorelin"]),

  // Recovery pairings
  "bpc-157":          Object.freeze(["tb-500", "ghk-cu", "kpv"]),
  "tb-500":           Object.freeze(["bpc-157", "ghk-cu", "kpv"]),

  // Copper / KPV
  "ghk-cu":           Object.freeze(["bpc-157", "tb-500", "kpv", "mots-c"]),
  "kpv":              Object.freeze(["bpc-157", "tb-500", "ghk-cu", "mots-c"]),

  // GH axis — never suggest the same active another strength.
  "cjc-1295":         Object.freeze(["ipamorelin", "ghk-cu", "bpc-157"]),
  "ipamorelin":       Object.freeze(["cjc-1295", "ghk-cu", "bpc-157"]),
  "sermorelin":       Object.freeze(["ipamorelin", "cjc-1295", "ghk-cu", "bpc-157"]),
  "tesamorelin":      Object.freeze(["ipamorelin", "cjc-1295", "ghk-cu", "bpc-157"]),

  // Mitochondrial / longevity
  "epithalon":        Object.freeze(["mots-c", "ss-31", "ghk-cu"]),
  "mots-c":           Object.freeze(["epithalon", "ss-31", "ghk-cu"]),
  "ss-31":            Object.freeze(["mots-c", "epithalon", "ghk-cu"]),

  // Neuropeptides
  "semax":            Object.freeze(["selank", "dsip"]),
  "selank":           Object.freeze(["semax", "dsip"]),
});

// ─── Catalog helpers ────────────────────────────────────────────────

/** Purchasable — identical to `ProductPurchaseActions.isPurchasable`. */
const _purchasable = (p) =>
  !!(p && p.slug && p.active && p.ecommerce_enabled &&
     p.checkout_status === "Add to Cart" &&
     p.card_payment_allowed &&
     p.public_price_visible &&
     (p.retail_price_cents || 0) > 0);

/**
 * Stable lookup: given a target family key, return the first eligible
 * catalog product (lowest price break, alphabetical tie-break) that:
 *   * is purchasable + in-stock,
 *   * is NOT already in the cart,
 *   * has NO family overlap with a family already excluded (cart or
 *     already-picked recommendation).
 */
function _resolveTarget(targetKey, catalog, excludeSlugs, excludeCompoundKeys) {
  const excludedSet = new Set(excludeCompoundKeys);
  const cands = catalog
    .filter(_purchasable)
    .filter((p) => !excludeSlugs.has(p.slug))
    .filter((p) => {
      const keys = compoundKeysForSlug(p.slug);
      // Product must include this target key AND
      // must NOT include any excluded family (cart/picked).
      if (!keys.includes(targetKey)) return false;
      for (const k of keys) if (excludedSet.has(k)) return false;
      return true;
    });

  if (cands.length === 0) return null;
  cands.sort((a, b) => {
    const pa = a.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
    const pb = b.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return String(a.slug).localeCompare(String(b.slug));
  });
  return cands[0];
}

// ─── Public engine ──────────────────────────────────────────────────

/**
 * Compute "Commonly researched together" recommendations for the
 * whole cart.
 *
 * @param {Array}  cartItems   Array of `{ slug, ... }` cart rows.
 * @param {Array}  catalog     Full catalog rows from `/api/products`.
 * @param {number} max         Hard cap (default 4).
 * @returns {Array}            Up to `max` purchasable catalog rows.
 */
export function recommendForCart(cartItems, catalog, max = 4) {
  if (!Array.isArray(cartItems) || !Array.isArray(catalog) || max <= 0) return [];

  const cartSlugs = new Set(
    cartItems.map((i) => i && i.slug).filter(Boolean),
  );
  const cartCompoundKeys = new Set();
  for (const it of cartItems) {
    if (!it || !it.slug) continue;
    for (const k of compoundKeysForSlug(it.slug)) cartCompoundKeys.add(k);
  }

  // ─── GLP outranks everything (priority rule #1) ───────────────────
  const glpInCart = cartCompoundKeys.has("glp");
  let targetKeys;

  if (glpInCart) {
    targetKeys = [...RULES["glp"]];
  } else {
    // Merge every triggered rule preserving each rule's internal order.
    // A target key added by an earlier trigger wins over a later
    // duplicate.  Cart compound keys are never valid targets.
    const merged = [];
    for (const trigger of cartCompoundKeys) {
      const rule = RULES[trigger];
      if (!rule) continue;
      for (const t of rule) {
        if (cartCompoundKeys.has(t)) continue;   // don't suggest same active
        if (!merged.includes(t)) merged.push(t);
      }
    }
    targetKeys = merged;
  }

  // Resolve each target key to a real catalog row and dedupe by family.
  const picked = [];
  const pickedCompoundKeys = new Set([...cartCompoundKeys]);

  for (const key of targetKeys) {
    if (picked.length >= max) break;
    if (pickedCompoundKeys.has(key)) continue;
    const p = _resolveTarget(key, catalog, cartSlugs, pickedCompoundKeys);
    if (!p) continue;
    picked.push(p);
    for (const k of compoundKeysForSlug(p.slug)) pickedCompoundKeys.add(k);
  }

  // ─── Fallback: distinct-family best sellers ───────────────────────
  if (picked.length < max) {
    const pool = catalog
      .filter(_purchasable)
      .filter((p) => !cartSlugs.has(p.slug))
      .filter((p) => {
        const keys = compoundKeysForSlug(p.slug);
        // Must have AT LEAST ONE compound key and none already picked/in-cart.
        if (keys.length === 0) return false;
        for (const k of keys) if (pickedCompoundKeys.has(k)) return false;
        return true;
      });
    pool.sort((a, b) => {
      const fa = !!b.is_featured - !!a.is_featured;
      if (fa !== 0) return fa;
      const pa = a.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
      const pb = b.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return String(a.slug).localeCompare(String(b.slug));
    });
    for (const p of pool) {
      if (picked.length >= max) break;
      const keys = compoundKeysForSlug(p.slug);
      if (keys.some((k) => pickedCompoundKeys.has(k))) continue;
      picked.push(p);
      for (const k of keys) pickedCompoundKeys.add(k);
    }
  }

  return picked.slice(0, max);
}

// ─── Copy constants (owner-approved, cautious) ──────────────────────
// Consumers (Cart.jsx, MiniCart.jsx) MUST use these exact strings so
// the Jest regression tests can lock the copy across releases.

export const CART_REC_HEADING = "Commonly researched together";
export const CART_REC_SUPPORT =
  "Based on commonly discussed research pairings and catalog patterns. Not evidence of safe combined use.";

// ─── Small selector for tests / callers that need it ────────────────
export function compoundKeysForCart(cartItems) {
  const s = new Set();
  for (const it of cartItems || []) {
    if (!it || !it.slug) continue;
    for (const k of compoundKeysForSlug(it.slug)) s.add(k);
  }
  return [...s];
}
